import { SyncErrorIcon } from '@/components/icons'
import diagnosticsApi from '@/api/operations/diagnosticsApi'
import { useAuthContext } from '@/features/auth/context'
import useResetIdFlowValve from '@/features/config/hooks/hydraulic/flowValve/useResetIdFlowValve'
import { usePermission } from '@/hooks/permissions'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import useTranslation from '@/hooks/useTranslation'
import classes from '@/features/config/pages/Soil/style.module.scss'

interface FlowValveTableProps {
  rows: any
}

const LOCAL_KEY_ERROR = 'flowvalve-id-error'

const FlowValveTable: FC<FlowValveTableProps> = ({ rows }) => {
  const { t } = useTranslation()

  const themeMode = useSelector(selectTheme)
  const { interfaceId } = useAuthContext()
  const { isViewOnly } = usePermission()
  const { mutate: resetById, isSuccess } = useResetIdFlowValve()

  const [arrayReset, setArrayReset] = useState<any>([])
  const [diagnosing, setDiagnosing] = useState<string | null>(null)
  const [diagByStation, setDiagByStation] = useState<Record<string, any>>({})

  const getKeyReset = localStorage.getItem(LOCAL_KEY_ERROR)

  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_KEY_ERROR)
    if (saved) {
      setArrayReset(JSON.parse(saved))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // errorName is frequently empty on flow valve errors, so fall back to the
  // flowValveError_<code> translation, then to the raw code.
  const getErrorMessage = (row: any): string => {
    const name = row.errorName?.trim()
    if (name) return name
    if (row.errorCode) {
      const key = `flowValveError_${row.errorCode}`
      const translated = t(key)
      if (translated && translated !== key) return translated
      return String(row.errorCode)
    }
    return '-'
  }

  const handleResetId = (id: any) => {
    if (isViewOnly) return

    if (getKeyReset === null || !getKeyReset?.includes(id)) {
      resetById({
        interfaceId: Number(interfaceId),
        id: id,
      })
      const arrayNew = [...arrayReset, id]
      setArrayReset(arrayNew)
      if (!isSuccess) {
        setTimeout(function () {
          setArrayReset([])
        }, 2000)
      }
    }
  }

  const handleDiagnose = async (row: any) => {
    if (isViewOnly) return

    try {
      const stationId = `${row.address}-${row.index}`
      setDiagnosing(stationId)
      const res = await diagnosticsApi.runDecoderDiagnostic({
        interfaceId: Number(interfaceId),
        decoderIds: [stationId],
      })

      // Normalize response to an array
      const data = Array.isArray(res as any)
        ? (res as any)
        : Array.isArray((res as any)?.data)
          ? (res as any).data
          : (res as any)?.result && Array.isArray((res as any).result)
            ? (res as any).result
            : (res as any)
              ? [res as any]
              : []

      const next: Record<string, any> = { ...diagByStation }
      for (const item of data) {
        const key = `${item.address}-${item.index}`
        next[key] = item
      }
      setDiagByStation(next)
    } catch (e) {
      console.error('diagnostics failed', e)
    } finally {
      setDiagnosing(null)
    }
  }

  return (
    <Stack flexGrow="1">
      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ minWidth: 550 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ paddingLeft: '22px' }}>
                {t('tableHeaderId')}
              </TableCell>
              <TableCell>{t('headers.pipeName')}</TableCell>
              <TableCell>{t('decoderAddress')}</TableCell>
              <TableCell>{t('errorCode')}</TableCell>
              <TableCell>{t('errorMessage')}</TableCell>
              <TableCell>{t('diagnosticReason')}</TableCell>
              <TableCell>{t('suggestedAction')}</TableCell>
              <TableCell align="right">
                {t('interfaceTableHeaderAction')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              backgroundColor: 'common.white',
              '& .MuiTableRow-root:nth-of-type(even)': {
                backgroundColor: '#F9F9F9',
              },
            }}
          >
            {rows.map((row: any) => {
              const stationKey = `${row.address}-${row.index}`
              const diag = diagByStation[stationKey]
              return (
                <TableRow key={row.id}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ paddingLeft: '22px' }}
                  >
                    {row.id}
                  </TableCell>
                  <TableCell>{row.pipeName}</TableCell>
                  <TableCell>{`${row.address}-${row.index}`}</TableCell>
                  <TableCell>{row.errorCode}</TableCell>
                  <TableCell sx={{ color: '#FF3030' }}>
                    {diag?.error?.description ?? getErrorMessage(row)}
                  </TableCell>
                  <TableCell>{diag?.error?.reason ?? ''}</TableCell>
                  <TableCell>
                    {diag?.error?.action ? (
                      <Typography
                        component="div"
                        sx={{ whiteSpace: 'pre-line' }}
                      >
                        {diag.error.action}
                      </Typography>
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      spacing={2}
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleDiagnose(row)}
                        disabled={diagnosing === stationKey}
                        sx={{
                          opacity: isViewOnly ? 0.5 : 1,
                          cursor: isViewOnly ? 'not-allowed' : 'pointer',
                          pointerEvents: isViewOnly ? 'none' : 'auto',
                        }}
                      >
                        {diagnosing === stationKey ? 'Diagnosing…' : 'Diagnose'}
                      </Button>
                      <Stack
                        className={classes.txtErTable}
                        sx={{
                          opacity: isViewOnly ? 0.5 : 1,
                          cursor: isViewOnly ? 'not-allowed' : 'pointer',
                          pointerEvents: isViewOnly ? 'none' : 'auto',
                        }}
                      >
                        <Box
                          component="span"
                          className={classes.txtBox}
                          onClick={() => handleResetId(row.id)}
                          sx={{
                            color:
                              themeMode === mode_type.dark
                                ? '#fff !important'
                                : '#000 !important',
                          }}
                        >
                          {arrayReset?.includes(row.id) ? (
                            <LoadingButton
                              loading
                              loadingPosition="start"
                              startIcon={<SyncErrorIcon />}
                            >
                              {t('errors.acknowledge')}
                            </LoadingButton>
                          ) : (
                            <>
                              <SyncErrorIcon sx={{ marginRight: '4px' }} />{' '}
                              {t('errors.acknowledge')}
                            </>
                          )}
                        </Box>
                      </Stack>
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default FlowValveTable
